import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, X } from 'lucide-react';
import { useDialogA11y } from '../utils/useDialogA11y';

// Fix A11Y-5: added Ctrl+B/I/U formatting shortcuts to match the actual handlers.
const SHORTCUT_LINES = [
  { keys: 'Space (hold)', action: 'Pan document' },
  { keys: 'Enter / F2', action: 'Edit selected field' },
  { keys: 'Delete', action: 'Remove selected field' },
  { keys: 'Arrow keys', action: 'Nudge field (Shift = 10×)' },
  { keys: 'Ctrl+Z / Ctrl+Y', action: 'Undo / Redo' },
  { keys: 'Ctrl+C / Ctrl+V', action: 'Copy / paste field across pages' },
  { keys: 'Ctrl+D', action: 'Duplicate field (Shift = all pages)' },
  { keys: 'Ctrl+V', action: 'Paste PDF from clipboard (when no field copied)' },
  { keys: 'Ctrl+B', action: 'Bold (text fields)' },
  { keys: 'Ctrl+I', action: 'Italic (text fields)' },
  { keys: 'Ctrl+U', action: 'Underline (text fields)' },
  { keys: 'T', action: 'Text tool' },
  { keys: 'V', action: 'Select tool' },
  { keys: 'S', action: 'Save snapshot' },
  { keys: 'P', action: 'Toggle pan mode' },
  { keys: '?', action: 'Show shortcuts help' },
  { keys: 'Esc', action: 'Cancel placement / close dialogs' },
];

const SHORTCUTS_TITLE = SHORTCUT_LINES.map((l) => `${l.keys} — ${l.action}`).join('\n');

interface StatusBarProps {
  message: string;
  pageIndex?: number;
  pageCount?: number;
  zoomPercent?: number;
  fieldCount?: number;
  isDirty?: boolean;
  fileIdPreview?: string | null;
  /** When provided, the page indicator becomes an editable go-to-page input. */
  onPageJump?: (pageIndex: number) => void;
  shortcutsOpen?: boolean;
  onShortcutsOpenChange?: (open: boolean) => void;
}

export const StatusBar: React.FC<StatusBarProps> = ({
  message,
  pageIndex = 0,
  pageCount = 0,
  zoomPercent,
  fieldCount = 0,
  isDirty = false,
  fileIdPreview = null,
  onPageJump,
  shortcutsOpen: shortcutsOpenProp,
  onShortcutsOpenChange,
}) => {
  const [pageDraft, setPageDraft] = useState(String(pageIndex + 1));
  const isEditingRef = useRef(false);
  const [shortcutsOpenInternal, setShortcutsOpenInternal] = useState(false);
  const shortcutsOpen = shortcutsOpenProp ?? shortcutsOpenInternal;
  const setShortcutsOpen = onShortcutsOpenChange ?? setShortcutsOpenInternal;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Sync the page-number input when the active page changes due to scrolling,
  // but only when the user is not actively typing a jump target.
  useEffect(() => {
    if (!isEditingRef.current) {
      setPageDraft(String(pageIndex + 1));
    }
  }, [pageIndex]);
  // "Fresh" success state: a brief is-fresh class is added when the
  // status message changes to a known success tone. The CSS animates
  // a soft green pulse. After 1.2s the class is removed so the next
  // success can re-trigger the animation.
  const [isFresh, setIsFresh] = useState(false);
  const prevMessageRef = useRef(message);
  useEffect(() => {
    if (message === prevMessageRef.current) return;
    prevMessageRef.current = message;
    const lower = message.toLowerCase();
    if (
      lower.includes('ready') ||
      lower.includes('saved') ||
      lower.includes('signed') ||
      lower.includes('snapshot saved') ||
      lower.includes('placed')
    ) {
      setIsFresh(true);
      const t = window.setTimeout(() => setIsFresh(false), 1200);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [message]);

  const closeShortcuts = useCallback(() => setShortcutsOpen(false), []);
  // Focus trap + Escape-to-close + scroll lock for the shortcuts dialog (a11y).
  useDialogA11y(shortcutsOpen, closeShortcuts, dialogRef);

  const commitPageJump = () => {
    if (!onPageJump || pageCount < 1) return;
    const parsed = Number.parseInt(pageDraft, 10);
    if (Number.isNaN(parsed)) {
      setPageDraft(String(pageIndex + 1));
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), pageCount);
    setPageDraft(String(clamped));
    if (clamped - 1 !== pageIndex) onPageJump(clamped - 1);
  };

  return (
    <>
      <footer className="editor-statusbar" role="status" aria-live="polite">
        <div className="editor-statusbar-left">
          {pageCount > 0 &&
            (onPageJump ? (
              <span className="editor-statusbar-stat editor-statusbar-pagejump">
                Page{' '}
                <input
                  type="number"
                  min={1}
                  max={pageCount}
                  value={pageDraft}
                  onChange={(e) => setPageDraft(e.target.value)}
                  onFocus={() => { isEditingRef.current = true; }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      isEditingRef.current = false;
                      commitPageJump();
                      (e.target as HTMLInputElement).blur();
                    } else if (e.key === 'Escape') {
                      isEditingRef.current = false;
                      setPageDraft(String(pageIndex + 1));
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onBlur={() => { isEditingRef.current = false; commitPageJump(); }}
                  aria-label={`Go to page (1 to ${pageCount})`}
                  title="Type a page number and press Enter"
                />{' '}
                / {pageCount}
              </span>
            ) : (
              <span className="editor-statusbar-stat">
                Page {pageIndex + 1} / {pageCount}
              </span>
            ))}
          {zoomPercent != null && (
            <span className="editor-statusbar-stat">{Math.round(zoomPercent * 100)}%</span>
          )}
          <span className="editor-statusbar-stat">{fieldCount} field{fieldCount === 1 ? '' : 's'}</span>
          {isDirty && <span className="editor-statusbar-dot">Unsaved changes</span>}
          {/* Fix A11Y-5: removed redundant aria-live="polite" — the parent
               <footer role="status" aria-live="polite"> already covers this region.
               Double aria-live causes screen readers to announce the message twice. */}
          <span className={`editor-statusbar-message${isFresh ? ' is-fresh' : ''}`}>
            {message}
          </span>
          <button
            type="button"
            className="editor-statusbar-kbd"
            title={SHORTCUTS_TITLE}
            aria-label="Keyboard shortcuts"
            aria-haspopup="dialog"
            aria-expanded={shortcutsOpen}
            onClick={() => setShortcutsOpen(true)}
          >
            <Keyboard size={14} strokeWidth={1.75} />
          </button>
        </div>
        {fileIdPreview && (
          <span className="editor-statusbar-meta">file: {fileIdPreview.slice(0, 8)}…</span>
        )}
      </footer>

      {shortcutsOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => setShortcutsOpen(false)}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="shortcuts-dialog-title"
            tabIndex={-1}
            className="modal w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900 outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
              <h2 id="shortcuts-dialog-title" className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Keyboard shortcuts
              </h2>
              <button
                type="button"
                onClick={() => setShortcutsOpen(false)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Close shortcuts"
              >
                <X size={16} />
              </button>
            </div>
            <ul tabIndex={-1} className="max-h-[60vh] overflow-y-auto px-4 py-3 text-sm">
              {SHORTCUT_LINES.map((line) => (
                <li
                  key={line.keys}
                  className="flex items-baseline justify-between gap-4 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800"
                >
                  <kbd className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {line.keys}
                  </kbd>
                  <span className="text-right text-slate-600 dark:text-slate-400">{line.action}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};